import { ErrorResponse, type UserManager } from 'oidc-client-ts';

import { createAuthSession, isTerminalOAuthErrorCode, type AuthSession } from '@ngsos/portal-core';

const REFRESH_LOCK_NAME = 'ngsos-oidc-refresh';

async function withRefreshLockHeldAcrossTabs<T>(run: () => Promise<T>): Promise<T> {
  if (navigator.locks === undefined) {
    return run();
  }
  return navigator.locks.request(REFRESH_LOCK_NAME, run);
}

export function createWebAuthSession(userManager: UserManager): AuthSession {
  return createAuthSession({
    async readTokens() {
      const user = await userManager.getUser();
      return {
        accessToken: user?.access_token ?? null,
        expiresInSeconds: user?.expires_in ?? null,
        hasRefreshToken: user?.refresh_token != null,
      };
    },
    async refresh() {
      return (await userManager.signinSilent())?.access_token ?? null;
    },
    isRefreshTokenDefinitivelyRejected(error) {
      return error instanceof ErrorResponse && isTerminalOAuthErrorCode(error.error);
    },
    async clearTokens() {
      await userManager.removeUser();
    },
    runExclusively: withRefreshLockHeldAcrossTabs,
    onExternalSessionEnd(notify) {
      userManager.events.addUserSignedOut(notify);
    },
  });
}
